'use client'

import Image from 'next/image'
import { ArrowUpRight } from 'lucide-react'
import PocapanImage from '@/assets/images/Section1_Pocapan.png'
import type { SiteCopy } from '../content'
import styles from './ServiceShowcase.module.css'
import BettieConversation from './BettieConversation'

const services = [
  { id: 'bettie', name: 'Bettie', logo: 'bettie-rainbow.png', logoWidth: 683, logoHeight: 198, playStoreUrl: 'https://play.google.com/store/apps/details?id=com.peacecover.bettie', appStoreUrl: 'https://apps.apple.com/app/id6769073576', image: '/images/peacecover/screenshots/bettie-chat.png', width: 1080, height: 2340 },
  { id: 'pocapan', name: 'Pocapan', logo: 'pocapan-purple.svg', logoWidth: 291, logoHeight: 33, playStoreUrl: 'https://play.google.com/store/apps/details?id=com.peacecover.pocapan', appStoreUrl: 'https://apps.apple.com/app/id6449088562', image: PocapanImage, width: 600, height: 1124 },
] as const

export default function ServiceShowcase({ copy }: { copy: SiteCopy }) {
  return (
    <section id="services" className={styles.section} aria-labelledby="services-heading">
      <div className={styles.inner}>
        <header className={styles.heading}>
          <p className={styles.eyebrow}>{copy.about.eyebrow}</p>
          <h2 id="services-heading">{copy.about.heading}</h2>
          <p className={styles.introduction}>{copy.about.subheading}</p>
        </header>

        <div className={styles.grid}>
          {services.map((service) => {
            const content = copy.about[service.id]
            return (
              <article id={service.id} key={service.id} className={`${styles.card} ${styles[service.id]}`} aria-labelledby={`${service.id}-heading`}>
                <div className={styles.cardHeader}>
                  <h3 id={`${service.id}-heading`}>
                    <span className={styles.logoStack}>
                      {service.id === 'bettie' && <Image src={`/images/peacecover/logos/${service.logo}`} alt="" aria-hidden="true" width={service.logoWidth} height={service.logoHeight} className={styles.logoGlow} />}
                      <Image src={`/images/peacecover/logos/${service.logo}`} alt={service.name} width={service.logoWidth} height={service.logoHeight} className={styles.serviceLogo} />
                    </span>
                  </h3>
                  <p className={styles.tagline}>{content.tagline}</p>
                </div>

                <figure className={styles.figure}>
                  <div className={styles.stage}>
                    {service.id === 'bettie' ? <div className={styles.conversationStage}>
                      <BettieConversation copy={copy.about.bettie.demo} />
                    </div> : <div className={styles.screenFrame}>
                      <Image src={service.image} alt={content.imageAlt} width={service.width} height={service.height} sizes="(max-width: 760px) 240px, 270px" className={styles.screen} />
                    </div>}
                  </div>
                </figure>

                <div className={styles.body}>
                  <p className={styles.description}>{content.description}</p>
                  <div className={styles.storeLinks}>
                  <a href={service.appStoreUrl} target="_blank" rel="noopener noreferrer" className={styles.link} aria-label={`${service.name} · ${content.cta}`}>{content.cta}<ArrowUpRight size={18} aria-hidden="true" /></a>
                    <a href={service.playStoreUrl} target="_blank" rel="noopener noreferrer" className={styles.link} aria-label={`${service.name} · ${content.playStoreCta}`}>{content.playStoreCta}<ArrowUpRight size={18} aria-hidden="true" /></a>
                  </div>
                </div>
              </article>
            )
          })}
        </div>
      </div>

    </section>
  )
}
